<script setup lang="ts">
import { computed, useAttrs } from 'vue';
import { iconToSVG } from '@iconify/utils/lib/svg/build';
import { getOfflineIcon } from '@/plugins/iconify';

defineOptions({ name: 'SvgIcon', inheritAttrs: false });

/**
 * Props
 *
 * - Support offline Iconify and local svg icon
 * - If icon and localIcon are passed at the same time, localIcon will be rendered first
 */
interface Props {
    /** Iconify icon name */
    icon?: string;
    /** Local svg icon name */
    localIcon?: string;
    /** Icon color */
    color?: string;
    /** Icon size */
    fontSize?: number | string;
}

const props = defineProps<Props>();

const attrs = useAttrs();

const safeAttrs = computed(() => {
    const nextAttrs: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(attrs)) {
        if (key.toLowerCase() === 'style') continue;
        if (/^on/i.test(key)) continue;

        nextAttrs[key] = value;
    }

    return nextAttrs;
});

const iconSize = computed(() => {
    if (!props.fontSize) return '16px';
    return typeof props.fontSize === 'number' ? `${props.fontSize}px` : props.fontSize;
});

const bindAttrs = computed(() => ({
    ...safeAttrs.value,
    width: iconSize.value,
    height: iconSize.value,
    ...(props.color ? { color: props.color } : {})
}));

const symbolId = computed(() => {
    const { VITE_ICON_LOCAL_PREFIX: prefix } = import.meta.env;

    const defaultLocalIcon = 'no-icon';

    const icon = props.localIcon || defaultLocalIcon;

    return `#${prefix}-${icon}`;
});

/** If localIcon is passed, render localIcon first */
const renderLocalIcon = computed(() => props.localIcon || !props.icon);

const iconName = computed(() => props.icon || '');

const renderIconifyIcon = computed(() => {
    if (!iconName.value || renderLocalIcon.value) {
        return false;
    }

    return Boolean(getOfflineIcon(iconName.value));
});

const iconSvg = computed(() => {
    if (!renderIconifyIcon.value) {
        return null;
    }

    const iconData = getOfflineIcon(iconName.value);
    if (!iconData) {
        return null;
    }

    return iconToSVG(iconData, {});
});

const iconSvgAttrs = computed(() => {
    if (!iconSvg.value) {
        return bindAttrs.value;
    }

    const { style: _style, ...svgAttributes } = iconSvg.value.attributes as Record<string, unknown>;

    return {
        ...svgAttributes,
        ...bindAttrs.value
    };
});
</script>

<template>
    <template v-if="renderLocalIcon">
        <svg aria-hidden="true" v-bind="bindAttrs">
            <use :xlink:href="symbolId" fill="currentColor" />
        </svg>
    </template>
    <template v-else>
        <svg
            v-if="iconSvg"
            aria-hidden="true"
            v-bind="iconSvgAttrs"
            v-html="iconSvg.body"
        />
        <svg v-else aria-hidden="true" v-bind="bindAttrs">
            <use :xlink:href="symbolId" fill="currentColor" />
        </svg>
    </template>
</template>

<style scoped></style>
